import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { ingestAPI } from '../../lib/api'
import { motion } from 'framer-motion'
import { FileText, Trash2, RefreshCw, CheckCircle, AlertCircle, Clock, Brain, Globe, Lock, Search } from 'lucide-react'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import toast from 'react-hot-toast'
import { formatFileSize } from '../../utils/helpers'
import { DocumentModal } from './DocumentModal'

interface DocumentResponse {
  id: string
  title: string
  file_type: string
  status: 'processing' | 'completed' | 'failed'
  chunks_count: number
  created_at: string
  file_size: string | number
  created_by?: string
  extraction_status?: 'pending' | 'processing' | 'ready_for_review' | 'completed' | 'failed'
  is_public?: boolean
  chunks_preview?: Array<{
    id: string
    preview: string
    index: number
  }>
}

interface DocumentListProps {
  onOpenChunkReview?: (documentId: string) => void
}

export function DocumentList({ onOpenChunkReview }: DocumentListProps) {
  const [selectedDoc, setSelectedDoc] = useState<DocumentResponse | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const queryClient = useQueryClient()

  // Fetch documents from API
  const { data: documents = [], isLoading, refetch, isRefetching } = useQuery<DocumentResponse[]>(
    'documents',
    async () => {
      const docs = await ingestAPI.getDocuments()
      console.log('Fetched documents:', docs) // Debug log
      // Log documents with chunks for debugging
      const docsWithChunks = docs.filter((d: any) => d.chunks_count > 0)
      console.log(`Documents with chunks: ${docsWithChunks.length}/${docs.length}`)
      if (docsWithChunks.length > 0) {
        console.log('Sample document with chunks:', docsWithChunks[0])
      }
      return docs as DocumentResponse[]
    },
    {
      refetchInterval: 5000, // Refetch every 5 seconds to update processing status
      refetchOnWindowFocus: false, // Prevent refetch on window focus
      retry: 1, // Only retry once on error
      staleTime: 1000, // Consider data stale after 1 second
      keepPreviousData: true, // Keep showing old data while fetching
      onError: (error) => {
        console.error('Error fetching documents:', error)
        // Only show error if not a background refetch
        if (!isRefetching) {
          toast.error('Fehler beim Laden der Dokumente')
        }
      }
    }
  )

  const deleteMutation = useMutation(
    (documentId: string) => ingestAPI.deleteDocument(documentId),
    {
      onSuccess: () => {
        toast.success('Dokument gelöscht')
        setSelectedDoc(null)
        queryClient.invalidateQueries('documents')
      },
      onError: () => {
        toast.error('Fehler beim Löschen')
      }
    }
  )

  const handleDelete = (documentId: string) => {
    if (confirm('Möchten Sie dieses Dokument wirklich löschen?')) {
      deleteMutation.mutate(documentId)
    }
  }

  const getStatusIcon = (doc: DocumentResponse) => {
    // First check if document processing failed
    if (doc.status === 'failed') {
      return <span title="Verarbeitung fehlgeschlagen"><AlertCircle className="w-4 h-4 text-red-500" /></span>
    }
    
    // If document is still being processed
    if (doc.status === 'processing') {
      return <span title="Dokument wird verarbeitet"><Clock className="w-4 h-4 text-blue-500 animate-pulse" /></span>
    }
    
    // Document is completed, now check chunk status
    if (doc.status === 'completed') {
      // If chunks are ready for review (extraction successful but waiting for approval)
      if (doc.extraction_status === 'ready_for_review') {
        return <span title="Bereit zur Überprüfung - Chunks extrahiert"><CheckCircle className="w-4 h-4 text-green-500" /></span>
      }
      // If chunks are fully completed and applied
      else if (doc.extraction_status === 'completed' || (doc.chunks_count && doc.chunks_count > 0)) {
        return <span title="Fertig - Dokument durchsuchbar"><CheckCircle className="w-4 h-4 text-green-500" /></span>
      } 
      // If chunks are being processed
      else if (doc.extraction_status === 'processing') {
        return <span title="Chunks werden erstellt"><Clock className="w-4 h-4 text-yellow-500 animate-pulse" /></span>
      }
      // If chunk creation failed
      else if (doc.extraction_status === 'failed') {
        return <span title="Chunk-Erstellung fehlgeschlagen"><AlertCircle className="w-4 h-4 text-red-500" /></span>
      }
      // Waiting for chunk creation to start
      else if (doc.extraction_status === 'pending') {
        return <span title="Wartet auf Chunk-Erstellung"><Clock className="w-4 h-4 text-yellow-500 animate-pulse" /></span>
      }
      // Default case - assume success if document is completed
      else {
        return <span title="Fertig - Dokument verarbeitet"><CheckCircle className="w-4 h-4 text-green-500" /></span>
      }
    }
    
    return null
  }

  const getFileIcon = (fileType: string | undefined) => {
    const type = fileType || '.txt'
    const color = {
      '.pdf': 'text-red-500',
      '.docx': 'text-blue-500',
      '.doc': 'text-blue-500',
      '.xlsx': 'text-green-500',
      '.xls': 'text-green-500',
      '.txt': 'text-gray-500',
      '.md': 'text-purple-500',
    }[type] || 'text-gray-500'

    return <FileText className={`w-5 h-5 ${color}`} />
  }

  const getFormattedFileSize = (size: string | number | undefined) => {
    if (!size || size === 'Unknown' || size === 'Unbekannt') return 'Unbekannt'
    
    const bytes = Number(size)
    if (isNaN(bytes) || bytes === 0) return 'Unbekannt'
    
    return formatFileSize(bytes)
  }

  // Filter documents based on search query
  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return documents
    
    const query = searchQuery.toLowerCase()
    return documents.filter(doc => 
      doc.title?.toLowerCase().includes(query) ||
      doc.file_type?.toLowerCase().includes(query) ||
      doc.created_by?.toLowerCase().includes(query)
    )
  }, [documents, searchQuery])

  const handleDocumentClick = (doc: DocumentResponse) => {
    setSelectedDoc(doc)
    setModalOpen(true)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    )
  }

  return (
    <>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Document List */}
      <div className="lg:col-span-2 space-y-4">
        {/* Search Bar */}
        <div className="glass rounded-lg p-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Dokumente durchsuchen (Titel, Typ, Ersteller)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">Dokumente ({filteredDocuments.length})</h3>
          <button 
            onClick={() => refetch()}
            disabled={isRefetching}
            className="btn btn-secondary btn-sm flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${isRefetching ? 'animate-spin' : ''}`} />
            <span>{isRefetching ? 'Lädt...' : 'Aktualisieren'}</span>
          </button>
        </div>

        {filteredDocuments.length === 0 ? (
          <div className="glass rounded-lg p-8 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-600" />
            <p className="text-gray-400">
              Noch keine Dokumente hochgeladen
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredDocuments.map((doc, index) => (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => {
                  console.log('Document clicked:', doc.id, doc.title)
                  handleDocumentClick(doc)
                }}
                className="glass rounded-lg p-4 transition-all hover:bg-white/20 hover:scale-[1.02] cursor-pointer select-none relative z-10"
                style={{ pointerEvents: 'auto' }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    {getFileIcon(doc.file_type)}
                    <div>
                      <h4 className="font-medium">{doc.title}</h4>
                      <div className="flex items-center space-x-4 mt-1 text-sm text-gray-400">
                        <span>{getFormattedFileSize(doc.file_size)}</span>
                        <span className="flex items-center space-x-1">
                          <span>Typ:</span>
                          <span>{doc.file_type || 'Unbekannt'}</span>
                        </span>
                        {doc.status === 'completed' && doc.chunks_count > 0 && (
                          <span>{doc.chunks_count} Chunks</span>
                        )}
                        <span>{format(new Date(doc.created_at), 'dd.MM.yyyy', { locale: de })}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2" style={{ pointerEvents: 'auto' }}>
                    {doc.is_public ? (
                      <span title="Öffentlich"><Globe className="w-4 h-4 text-green-400" /></span>
                    ) : (
                      <span title="Privat"><Lock className="w-4 h-4 text-gray-400" /></span>
                    )}
                    {getStatusIcon(doc)}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDelete(doc.id)
                      }}
                      className="p-1 hover:bg-white/10 rounded transition-smooth relative z-20"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Document Details */}
      <div className="lg:col-span-1">
        {selectedDoc ? (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="glass rounded-xl p-6 sticky top-6"
          >
            <h3 className="font-bold mb-4">Dokument-Details</h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-500">Titel</label>
                <p className="font-medium">{selectedDoc.title}</p>
              </div>
              
              <div>
                <label className="text-sm text-gray-500">Dateityp</label>
                <p>{selectedDoc.file_type || 'Unbekannt'}</p>
              </div>
              
              <div>
                <label className="text-sm text-gray-500">Größe</label>
                <p>{getFormattedFileSize(selectedDoc.file_size)}</p>
              </div>
              
              <div>
                <label className="text-sm text-gray-500">Status</label>
                <div className="flex items-center space-x-2 mt-1">
                  {getStatusIcon(selectedDoc)}
                  <span>
                    {selectedDoc.status === 'completed' ? 'Verarbeitet' :
                     selectedDoc.status === 'processing' ? 'Wird verarbeitet' :
                     'Fehler'}
                  </span>
                </div>
              </div>
              
              {selectedDoc.status === 'completed' && (
                <div>
                  <label className="text-sm text-gray-500">Chunks</label>
                  <p>{selectedDoc.chunks_count || 0} Textabschnitte</p>
                </div>
              )}
              
              <div>
                <label className="text-sm text-gray-500">Hochgeladen</label>
                <p>{format(new Date(selectedDoc.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}</p>
              </div>
              
              <div>
                <label className="text-sm text-gray-500">Sichtbarkeit</label>
                <div className="flex items-center space-x-2 mt-1">
                  {selectedDoc.is_public ? (
                    <>
                      <Globe className="w-4 h-4 text-green-400" />
                      <span>Öffentlich</span>
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 text-gray-400" />
                      <span>Privat</span>
                    </>
                  )}
                </div>
              </div>
              
              {selectedDoc.extraction_status && (
                <div>
                  <label className="text-sm text-gray-500">Extraktion</label>
                  <p className="capitalize">
                    {selectedDoc.extraction_status === 'ready_for_review' ? 'Bereit zur Überprüfung' : 
                     selectedDoc.extraction_status === 'processing' ? 'Wird extrahiert' :
                     selectedDoc.extraction_status === 'completed' ? 'Abgeschlossen' :
                     selectedDoc.extraction_status === 'failed' ? 'Fehlgeschlagen' : 'Ausstehend'}
                  </p>
                </div>
              )}
              
              <div className="pt-4 space-y-2">
                {selectedDoc.extraction_status === 'ready_for_review' && onOpenChunkReview && (
                  <button 
                    onClick={() => onOpenChunkReview(selectedDoc.id)}
                    className="w-full btn btn-primary flex items-center justify-center space-x-2"
                  >
                    <Brain className="w-4 h-4" />
                    <span>Chunks überprüfen</span>
                  </button>
                )}
                
                {selectedDoc.status === 'failed' && (
                  <button className="w-full btn btn-secondary flex items-center justify-center space-x-2">
                    <RefreshCw className="w-4 h-4" />
                    <span>Erneut verarbeiten</span>
                  </button>
                )}
                
                <button
                  onClick={() => handleDelete(selectedDoc.id)}
                  className="w-full btn btn-secondary text-red-400 hover:bg-red-500/20 flex items-center justify-center space-x-2"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Löschen</span>
                </button>
              </div>
            </div>
          </motion.div>
        ) : (
          <div className="glass rounded-xl p-6 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-600" />
            <p className="text-gray-400">
              Wählen Sie ein Dokument aus, um Details zu sehen
            </p>
          </div>
        )}
      </div>
    </div>

    {/* Document Modal */}
    {selectedDoc && (
      <DocumentModal
        document={selectedDoc}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onUpdate={() => queryClient.invalidateQueries('documents')}
      />
    )}
    </>
  )
}